import { useEffect, useState, type FormEvent } from "react";
import { startRegistration, type PublicKeyCredentialCreationOptionsJSON } from "@simplewebauthn/browser";
import { Check, Circle } from "lucide-react";
import AccountLayout, { FormMessage, SettingsSection } from "../components/AccountLayout";
import { Dialog } from "@/components/ui/Dialog";
import { fieldLabel } from "@/components/ui/kit";
import { identityApi, type IdentityPasskey, type IdentitySession } from "@/api/identity";
import { useFormatDate } from "@/hooks/useFormatDate";
import { Pager } from "@/components/ui/Pager";
import { clampPage } from "@/lib/paging";

const field = "flex min-w-0 flex-col gap-1.5";
const row = "flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[var(--divider)] py-3 first:border-t-0 first:pt-0";

function Rule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className={`flex items-center gap-2 text-sm ${ok ? "text-[var(--success)]" : "text-[var(--text-muted)]"}`}>
      {ok ? <Check size={13} aria-hidden="true" /> : <Circle size={11} aria-hidden="true" />}
      {children}
    </li>
  );
}

/** "Chrome on macOS" from a user agent; the raw string when nothing matches. */
function device(ua: string | null): string {
  if (!ua) return "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "";
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  return browser && os ? `${browser} on ${os}` : browser || os || ua;
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const filled = current.length > 0;
  const longEnough = password.length >= 8;
  const matches = password.length > 0 && password === confirm;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      await identityApi("POST", "/api/auth/change-password", { currentPassword: current, newPassword: password });
      setCurrent("");
      setPassword("");
      setConfirm("");
      setSuccess("Password changed. Your other devices were signed out.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't change the password. Try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <SettingsSection title="Password" description="Change the password you use to sign in.">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {error && <FormMessage kind="error">{error}</FormMessage>}
        {success && <FormMessage kind="success">{success}</FormMessage>}
        <label className={field}>
          <span className={fieldLabel}>Current password</span>
          <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} disabled={saving} />
        </label>
        <label className={field}>
          <span className={fieldLabel}>New password</span>
          <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={saving} />
        </label>
        <label className={field}>
          <span className={fieldLabel}>Confirm new password</span>
          <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={saving} />
        </label>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          <Rule ok={filled}>Current password entered</Rule>
          <Rule ok={longEnough}>At least 8 characters</Rule>
          <Rule ok={matches}>Both passwords match</Rule>
        </ul>
        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={saving || !filled || !longEnough || !matches}>
            {saving ? "Saving…" : "Change password"}
          </button>
        </div>
      </form>
    </SettingsSection>
  );
}

function PasskeysSection() {
  const { date } = useFormatDate();
  const [passkeys, setPasskeys] = useState<IdentityPasskey[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);

  const load = () =>
    identityApi<IdentityPasskey[]>("GET", "/api/auth/passkeys")
      .then(setPasskeys)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't load your passkeys."));
  useEffect(() => {
    void load();
  }, []);

  async function add(name: string) {
    setError(null);
    try {
      const options = await identityApi<PublicKeyCredentialCreationOptionsJSON>("POST", "/api/auth/passkeys/register/options", {});
      const attestation = await startRegistration({ optionsJSON: options });
      await identityApi("POST", "/api/auth/passkeys/register/verify", { attestation, name });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passkey setup failed or was cancelled.");
    }
  }

  async function rename() {
    if (!editing || !editing.name.trim()) return;
    setError(null);
    try {
      const updated = await identityApi<IdentityPasskey>("PATCH", `/api/auth/passkeys/${editing.id}`, { name: editing.name.trim() });
      setPasskeys((list) => list?.map((k) => (k.id === updated.id ? updated : k)) ?? null);
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't rename the passkey. Try again.");
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await identityApi("DELETE", `/api/auth/passkeys/${id}`);
      setPasskeys((list) => list?.filter((k) => k.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't remove the passkey. Try again.");
    }
  }

  return (
    <SettingsSection title="Passkeys" description="Sign in without a password using this device. Name each key so you know which is which.">
      {error && <FormMessage kind="error">{error}</FormMessage>}
      {passkeys === null ? (
        !error && <p className="m-0 text-sm text-[var(--text-muted)]">Loading…</p>
      ) : passkeys.length === 0 ? (
        <p className="m-0 text-sm text-[var(--text-muted)]">No passkeys yet.</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {passkeys.map((k) => (
            <li key={k.id} className={row}>
              {editing?.id === k.id ? (
                <>
                  <input
                    className="min-w-0 flex-1"
                    aria-label="Passkey name"
                    maxLength={100}
                    value={editing.name}
                    onChange={(e) => setEditing({ id: k.id, name: e.target.value })}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => void rename()}>
                    Save
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--text-primary)]">{k.name ?? "Unnamed passkey"}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      Added {date(k.createdAt)}
                      {k.lastUsedAt ? ` · last used ${date(k.lastUsedAt)}` : ""}
                    </div>
                  </div>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing({ id: k.id, name: k.name ?? "" })}>
                    Rename
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm text-[var(--danger)]" onClick={() => void remove(k.id)}>
                    Remove
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          className="btn btn-soft"
          onClick={() => {
            setNewName("");
            setNaming(true);
          }}
        >
          Add passkey
        </button>
      </div>
      {naming && (
        <Dialog title="Name the new passkey" onClose={() => setNaming(false)}>
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newName.trim()) return;
              setNaming(false);
              void add(newName.trim());
            }}
          >
            <p className="m-0 text-sm text-[var(--text-muted)]">Give this key a name first (e.g. MacBook Touch ID), then complete your device's prompt.</p>
            <label className={field}>
              <span className={fieldLabel}>Passkey name</span>
              <input required maxLength={100} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. MacBook Touch ID" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setNaming(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={!newName.trim()}>
                Continue
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </SettingsSection>
  );
}

const SESSIONS_PAGE = 10;

function SessionsSection() {
  const { dateTime } = useFormatDate();
  const [sessions, setSessions] = useState<IdentitySession[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    identityApi<IdentitySession[]>("GET", "/api/auth/sessions")
      .then(setSessions)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Couldn't load your sessions."));
  }, []);

  async function revoke(id: string) {
    setError(null);
    try {
      await identityApi("DELETE", `/api/auth/sessions/${id}`);
      setSessions((list) => list?.filter((s) => s.id !== id) ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't sign that device out. Try again.");
    }
  }

  // Signing a device out may empty the last page: step back to one that has rows.
  const shownPage = clampPage(page, SESSIONS_PAGE, sessions?.length ?? 0);
  const shown = sessions?.slice(shownPage * SESSIONS_PAGE, (shownPage + 1) * SESSIONS_PAGE) ?? [];

  return (
    <SettingsSection title="Sessions" description="Devices signed in as you. Sign out any you don't recognize.">
      {error && <FormMessage kind="error">{error}</FormMessage>}
      {sessions === null ? (
        !error && <p className="m-0 text-sm text-[var(--text-muted)]">Loading…</p>
      ) : (
        <>
          <ul className="m-0 list-none p-0">
            {shown.map((s) => (
              <li key={s.id} className={row}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-[var(--text-primary)]">
                    {device(s.userAgent)}
                    {s.current && <span className="pill bg-[var(--bg-card-elevated)] text-[var(--text-secondary)]">This device</span>}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]">
                    {[s.ip, `signed in ${dateTime(s.createdAt)}`].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {!s.current && (
                  <button type="button" className="btn btn-ghost btn-sm text-[var(--danger)]" onClick={() => void revoke(s.id)}>
                    Sign out
                  </button>
                )}
              </li>
            ))}
          </ul>
          <Pager page={shownPage} pageSize={SESSIONS_PAGE} total={sessions.length} onPage={setPage} />
        </>
      )}
    </SettingsSection>
  );
}

export default function AccountSecurity() {
  // Passkeys stay hidden while the SSO's PASSKEYS_ENABLED switch is off.
  const [passkeysOn, setPasskeysOn] = useState(false);
  useEffect(() => {
    identityApi<{ passkey?: boolean }>("GET", "/api/auth/methods")
      .then((m) => setPasskeysOn(m.passkey === true))
      .catch(() => {});
  }, []);
  return (
    <AccountLayout>
      <PasswordSection />
      {passkeysOn && <PasskeysSection />}
      <SessionsSection />
    </AccountLayout>
  );
}
